import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { IUserSessionComponentProps } from '../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../models/Logging';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { MainContainer } from '../../containers/MainContainer';
import { TelephonyContextProvider } from '../../contexts/telephony';
import { UserSessionViewModelKey } from '../../models/AppState';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, IUserSessionComponentProps, IEventLoggingComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	children?: React.ReactNode;
}

interface IState {
	hasError: boolean;
	error: Error;
	componentStack?: string;
}

class _MainContainerErrorBoundary extends React.Component<IProps, IState> {
	public readonly state: IState = { error: null, hasError: false };

	public static getDerivedStateFromError() {
		return { hasError: true };
	}

	public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		const { logError } = this.props;
		this.setState({ error, componentStack: errorInfo?.componentStack });

		logError('AIDAErrorBoundary-Error', error, errorInfo);
	}

	public render() {
		const { styles, className, userSession } = this.props;
		const { hasError, error, componentStack } = this.state;
		const islocalhost = location.href.includes('localhost');
		const userIsAida = userSession?.user?.primaryEmail?.value?.toLowerCase().includes('aida');

		if (hasError) {
			return (
				<TelephonyContextProvider>
					<MainContainer>
						<div className={css(styleSheet.center)}>
							<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
								<WarningIcon />
								{islocalhost && !userIsAida && (
									<div className={css(styleSheet.wrong)}>You&apos;re logged in as the wrong user</div>
								)}
								<div>COFFEE encountered an error. Please notify the engineering team if this problem persists.</div>
								<div className={css(styleSheet.message)}>{error?.message}</div>
								<div className={css(styleSheet.stack)}>{error?.stack}</div>
								{componentStack && (
									<>
										<div className={css(styleSheet.message)}>Component Stack:</div>
										<div className={css(styleSheet.stack)}>{componentStack}</div>
									</>
								)}
							</div>
						</div>
					</MainContainer>
				</TelephonyContextProvider>
			);
		}

		return this.props.children;
	}
}

const MainContainerErrorBoundaryAsObserver = observer(_MainContainerErrorBoundary);
const MainContainerErrorBoundaryWithRouter = withRouter(MainContainerErrorBoundaryAsObserver);
const MainContainerErrorBoundaryWithEventLogging = withEventLogging(MainContainerErrorBoundaryWithRouter);
export const MainContainerErrorBoundary = inject(UserSessionViewModelKey)(MainContainerErrorBoundaryWithEventLogging);
